import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { AssignmentTurnedIn, DashboardCustomize, Reviews, Storm } from '@mui/icons-material';
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import Feedback from "../Feedback/Feedback";
import ReminderTable from './ReminderTable';
import ForecastingTable from './ForecastingTable';
import ActiveProjectsTable from './ActiveProjectsTable';
import WeeklyQuotesReportModule from '../DashboardModules/WeeklyQuotesReport/WeeklyQuotesReport';

const modulesConfig = {

    'revenue-lines-flat': () => import('../DashboardModules/Forecasting/Forecasting')
    // Add more as needed
  };

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`dashboard-tabpanel-${index}`} aria-labelledby={`dashboard-tab-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `dashboard-tab-${index}`,
        'aria-controls': `dashboard-tabpanel-${index}`,
    };
}

const Content = () => {
    const [reminderData, setReminderData] = useState([])
    const [forecastOpportunities, setForecastOpportunities] = useState([])
    const [activeProjects, setActiveProjects] = useState([])
    const [loadedModules, setLoadedModules] = useState([]);
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""}) 
    const [tabValue, setTabValue] = useState(0);
    const { user } = useAuth();

    const handleTabChange = (event, newValue) => setTabValue(newValue);

    const handleRemoveModule = (modKey) => {
      setLoadedModules(prev =>
        prev.filter(module => module.key !== modKey)
      );
    };

    useEffect(() => {
        let cancelled = false;
        async function getData() {
          const reminderResponse = await API.Reporting.RSMReminder()
          if (cancelled) return;
          setReminderData(reminderResponse.data)
          const dashboardForecastingResponse = await API.Reporting.DashboardForecasting()
          if (cancelled) return;
          setForecastOpportunities(dashboardForecastingResponse.data)
          const activeProjectsResponse = await API.Reporting.DashboardActiveProjects()
          if (cancelled) return;
          setActiveProjects(activeProjectsResponse.data)
          const userDashboardResponse = await API.Dashboard.FetchByUser(user?.id || user?.userId);
          if (cancelled) return;

          if (userDashboardResponse) {

            const tempModules = [];

            for (const modKey of userDashboardResponse.modules || []) {
              if (modKey === 'revenue-lines-flat' && !['admin', 'manager'].includes(user?.role)) continue;
              const importer = modulesConfig[modKey];
              if (importer) {
                const LazyComponent = lazy(importer);

                // Wrap component in a function that injects props
                const wrappedComponent = (
                  <LazyComponent
                    key={modKey}
                    removeModule={() => handleRemoveModule(modKey)} // 👈 send callback
                    setFeedback={setFeedback} // 👈 add this line
                  />
                );

                tempModules.push(wrappedComponent);
              }
            }

            setLoadedModules(tempModules);
          }
        }

        getData();
        return () => { cancelled = true; };
      }, [user?.id, user?.userId, user?.role]);

    return (
        <>
            <Feedback props={feedback} setFeedback={setFeedback} />
            <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="dashboard tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            maxWidth: "100%",
                            "& .MuiTabs-scroller": {
                                overflowX: "auto !important",
                            },
                            "& .MuiTab-root": {
                                flexShrink: 0,
                                minWidth: { xs: 120, sm: 160 },
                            },
                        }}
                    >
                        <Tab icon={<AssignmentTurnedIn />} label="Active Projects" {...a11yProps(0)} />
                        <Tab icon={<Reviews />} label="Spotlight" {...a11yProps(1)} />
                        <Tab icon={<Storm />} label="Forecasting" {...a11yProps(2)} />
                        <Tab icon={<DashboardCustomize />} label="Modules" {...a11yProps(3)} />
                    </Tabs>
                </Box>

                <CustomTabPanel value={tabValue} index={0}>
                    <div className="row">
                        <ActiveProjectsTable projects={activeProjects} />
                    </div>
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={2}>
                    <div className="row">
                        <ForecastingTable opportunities={forecastOpportunities} setOpportunities={setForecastOpportunities} setFeedback={setFeedback} />
                    </div>
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={1}>
                    <div className="row">
                        <ReminderTable data={reminderData} />
                    </div>
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={3}>
                    <div className="row">
                        <WeeklyQuotesReportModule />
                        <Suspense fallback={<div>Loading modules...</div>}>
                            {loadedModules.map((mod, idx) => (
                                <React.Fragment key={idx}>{mod}</React.Fragment>
                            ))}
                        </Suspense>
                    </div>
                </CustomTabPanel>
            </Box>
        </>
    )    
}

export default Content
